import type { TextVariant } from "../domain/types";

export type TEmailBuilderCanvasBlockRailAction =
  | "move-earlier"
  | "move-later"
  | "merge-tag"
  | "bold"
  | "italic"
  | "underline"
  | "strikethrough"
  | "bulleted-list"
  | "numbered-list"
  | "link"
  | "settings"
  | "duplicate"
  | "delete";

export function emailBuilderCanvasBlockReorderRailShortcutLabel(
  intent: "move-earlier" | "move-later",
  isMac: boolean,
): string {
  if (isMac) {
    return intent === "move-earlier" ? "⌥⇧↑" : "⌥⇧↓";
  }

  return intent === "move-earlier" ? "Alt+Shift+↑" : "Alt+Shift+↓";
}

export function emailBuilderCanvasBlockRailActionShortcutLabel(
  action: TEmailBuilderCanvasBlockRailAction,
  isMac: boolean,
): string | null {
  switch (action) {
    case "move-earlier":
    case "move-later":
      return emailBuilderCanvasBlockReorderRailShortcutLabel(action, isMac);
    case "duplicate":
      return isMac ? "⌘D" : "Ctrl+D";
    case "delete":
      return isMac ? "⌫" : "Delete";
    case "bold":
      return isMac ? "⌘B" : "Ctrl+B";
    case "italic":
      return isMac ? "⌘I" : "Ctrl+I";
    case "underline":
      return isMac ? "⌘U" : "Ctrl+U";
    case "strikethrough":
      return isMac ? "⌘⇧X" : "Ctrl+Shift+X";
    default:
      return null;
  }
}

export function emailBuilderCanvasBlockRailActionTooltip(
  action: TEmailBuilderCanvasBlockRailAction,
  label: string,
  isMac: boolean,
): string {
  const shortcut = emailBuilderCanvasBlockRailActionShortcutLabel(action, isMac);
  return shortcut === null ? label : `${label} (${shortcut})`;
}

export function emailBuilderCanvasBlockReorderRailTooltip(
  intent: "move-earlier" | "move-later",
  moveLabel: string,
  isMac: boolean,
): string {
  return emailBuilderCanvasBlockRailActionTooltip(intent, moveLabel, isMac);
}

export type TEmailBuilderLayoutColumnRailAction = "move-left" | "move-right" | "delete";

export function emailBuilderLayoutColumnRailActionShortcutLabel(
  action: TEmailBuilderLayoutColumnRailAction,
  isMac: boolean,
): string {
  switch (action) {
    case "move-left":
      return isMac ? "⌥⇧←" : "Alt+Shift+←";
    case "move-right":
      return isMac ? "⌥⇧→" : "Alt+Shift→";
    case "delete":
      return isMac ? "⌫" : "Delete";
  }
}

export function emailBuilderLayoutColumnRailActionTooltip(
  action: TEmailBuilderLayoutColumnRailAction,
  label: string,
  isMac: boolean,
): string {
  const shortcut = emailBuilderLayoutColumnRailActionShortcutLabel(action, isMac);
  return `${label} (${shortcut})`;
}

export const EMAIL_BUILDER_CANVAS_BLOCK_CONTENT_RAIL_ACTIONS: readonly TEmailBuilderCanvasBlockRailAction[] =
  ["bold", "italic", "underline", "strikethrough", "link", "numbered-list", "bulleted-list", "merge-tag"];

export type TEmailBuilderCanvasBlockFormattingRailTextVariant = TextVariant | "button";

function emailBuilderCanvasBlockFormattingIncludesLists(
  textVariant?: TEmailBuilderCanvasBlockFormattingRailTextVariant,
): boolean {
  return textVariant === undefined || textVariant === "normal";
}

export function resolveEmailBuilderCanvasBlockFormattingRailActions(
  textVariant?: TEmailBuilderCanvasBlockFormattingRailTextVariant,
): TEmailBuilderCanvasBlockRailAction[] {
  if (textVariant === "button") {
    return ["bold", "italic", "underline", "strikethrough", "merge-tag"];
  }

  const includeLists = emailBuilderCanvasBlockFormattingIncludesLists(textVariant);
  return [
    "bold",
    "italic",
    "underline",
    "strikethrough",
    "link",
    ...(includeLists ? (["numbered-list", "bulleted-list"] as const) : []),
    "merge-tag",
  ];
}

export function shouldSuppressEmailBuilderCanvasClearForTextSelection(args: {
  hasNonCollapsedSelection: boolean;
  selectionInsideCanvasContentEditable: boolean;
}): boolean {
  return args.hasNonCollapsedSelection && args.selectionInsideCanvasContentEditable;
}
